import React, { useCallback, useState } from 'react';
import { Menu } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { ContentTransition } from '@/components/ui/ContentTransition';
import { usePreloadPages } from '@/hooks/app/usePreloadPages';
import { useMountDebug } from '@/hooks/shared/useMountDebug';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { OrgPresetRouteGuard } from './OrgPresetRouteGuard';
import { useApplyOrgBranding } from '@/hooks/shared/useApplyOrgBranding';
// Sonax widget temporarily disabled

interface AppLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_PINNED_KEY = 'sidebar-pinned';

export const AppLayout = ({ children }: AppLayoutProps) => {
  useMountDebug('AppLayout');
  // Pré-carregar páginas para melhor performance do sidebar
  usePreloadPages();
  // Tema/favicon/título do preset de interface da org (Atticus.hub etc.). Sem preset, no-op.
  useApplyOrgBranding();

  const location = useLocation();
  const noShell = location.pathname.startsWith('/activities/focus');

  const [sidebarPinned, setSidebarPinned] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_PINNED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebarPinned = useCallback(() => {
    setSidebarPinned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_PINNED_KEY, String(next));
      } catch {
        // ignore storage errors (private mode)
      }
      return next;
    });
  }, []);

  if (noShell) {
    return (
      <div
        className="h-screen w-screen overflow-hidden flex flex-col"
        style={{ background: 'hsl(var(--background))' }}
      >
        <ImpersonationBanner />
        <div className="flex-1 min-h-0 overflow-hidden"><OrgPresetRouteGuard>{children}</OrgPresetRouteGuard></div>
      </div>
    );
  }

  // Sonax widget temporarily disabled

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: 'hsl(var(--background))' }}>
      <ImpersonationBanner />
      {/* Navbar - dark gradient proprio */}
      <div className="flex-shrink-0 z-50 relative sidebar-gradient-bg"
           style={{ background: 'linear-gradient(to right, hsl(0 0% 0%) 0%, hsl(280 54% 22%) 100%)' }}>
        <Navbar />
      </div>
      
      {/* Container horizontal para sidebar e conteúdo */}
      <div className="flex-1 flex min-w-0 overflow-hidden relative">
        {/* Sidebar - usa bg-transparent para mostrar o gradiente do container */}
        {/* Sidebar agora é fixed, então não ocupa espaço no flex */}
        <Sidebar className="hidden h-full lg:flex" collapsed={!sidebarPinned} onToggle={toggleSidebarPinned} />

        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" aria-label="Abrir menu de navegação"
              className="absolute left-3 top-3 z-40 lg:hidden">
              <Menu className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 border-0 bg-black p-0">
            <Sidebar className="!static !flex !h-full w-full shadow-none" collapsed={false} />
          </SheetContent>
        </Sheet>

        {/* Main Content Area - mantém o fundo claro original */}
        {/* Padding-left acompanha a largura da sidebar: 5.5rem colapsada, ou pin (~200/224px) */}
        <div
          className={cn(
            'flex-1 flex flex-col min-w-0 relative overflow-hidden bg-background',
            'transition-[padding] duration-300 ease-in-out',
            'max-lg:!pl-0',
            sidebarPinned ? 'app-content-sidebar-pinned' : 'app-content-sidebar-collapsed'
          )}
        >
          {/* Scrollable Content Area */}
          <div className="flex-1 min-h-0 overflow-hidden w-full relative">
            <ContentTransition>
              <div className="h-full w-full min-h-0">
                <OrgPresetRouteGuard>{children}</OrgPresetRouteGuard>
              </div>
            </ContentTransition>
          </div>
        </div>
      </div>
    </div>
  );
};
